import { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { secondaryMarketCpOrderAmendResetTabThunk } from 'state/secondaryMarket/cpOrder/amend/amendedOrder.thunks';
import { reloadTabThunk } from 'state/secondaryMarket/cpOrder/cpOrder.thunks';
import {
  makeGetAmendCpOrderValidationErrors,
  makeGetModifiedFields,
} from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.selectors';
import type { SecondaryMarketCpOrderForm } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import { fromSecondaryMarket, fromSecondaryPostEventsWorkspace } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import {
  getSecondaryMarketQuoteErrorKind,
  getSecondaryMarketQuoteErrorMessage,
} from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { getTabIsFrozen, getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';

type OwnProps = Readonly<{
  tabId: string;
  sidePanel: ReactNode;
}>;

type StateProps = Readonly<{
  productName: string | undefined;
  ric: string | undefined;
  validationErrors: SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];
  traderLabel: string | undefined;
  bcsProductKey: BcsProductKey | undefined;
  hasFatalError: boolean;
  isLoading: boolean;
  modifiedFields: string[];
  canResetForm: boolean;
}>;

type DispatchProps = Readonly<{
  onResetForm: () => void;
  onRetryProductFetch?: () => void;
}>;

export type CpOrderFormProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getModifiedFields = makeGetModifiedFields();
  const getErrors = makeGetAmendCpOrderValidationErrors(getModifiedFields);
  return (state, { tabId }) => {
    const bcsProductKey = fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
    const dataLoadingStatus = fromSecondaryPostEventsWorkspace.getSecondaryMarketPostEventLoadingStatus(state, tabId);
    const tabContentLoadingStatus = getTabLoadingStatus(state, tabId);
    const productName = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductName(state, tabId);
    const ric = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductRic(state, tabId);
    const traderLabel = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductTraderLabel(state, tabId);
    const validationErrors = getErrors(state, tabId);
    const modifiedFields = getModifiedFields(state, tabId);
    const isFrozen = getTabIsFrozen(state, tabId) ?? false;
    const canResetForm = !isFrozen;
    const hasFatalError = tabContentLoadingStatus === TabLoadingStatus.LoadingFailed || dataLoadingStatus === 'failed';
    const isLoading =
      tabContentLoadingStatus === TabLoadingStatus.Loading ||
      dataLoadingStatus === 'loading' ||
      dataLoadingStatus === 'not_yet';
    const errorKind = getSecondaryMarketQuoteErrorKind(state, tabId);
    const errorMessage = getSecondaryMarketQuoteErrorMessage(state, tabId);
    return {
      productName,
      ric,
      hasFatalError,
      isLoading,
      validationErrors,
      traderLabel,
      bcsProductKey,
      modifiedFields,
      canResetForm,
      errorKind,
      errorMessage,
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onResetForm() {
      dispatch(secondaryMarketCpOrderAmendResetTabThunk(tabId));
    },
    onRetryProductFetch() {
      dispatch(reloadTabThunk(tabId));
    },
  };
};

export type ResetFormProps = OwnProps & StateProps & DispatchProps;

export const connectCpOrderForm = connect(mapStateToProps, mapDispatchToProps);
